import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpenCheck, Plus, Eye, Send, CheckCircle, XCircle, Clock,
  AlertTriangle, ChevronRight, FileText, RotateCcw, Filter,
  TrendingUp, Users, BarChart3, CheckSquare, X, Save, Loader2,
  ScanEye, ThumbsUp, Settings,
} from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { YearContext } from '../components/Layout/Layout';
import { plannerAPI, schoolConfigAPI } from '../services/api';
import LessonPlannerDashboard from './LessonPlannerDashboard';
import LessonPlannerSettings  from './LessonPlannerSettings';
import { PageLoader } from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

// ─── Status Badge ─────────────────────────────────────────────
const STATUS_META = {
  draft:                  { label: 'Draft',               color: 'bg-slate-100 text-slate-600' },
  submitted:              { label: 'Submitted',            color: 'bg-blue-100 text-blue-700' },
  under_review:           { label: 'Under Review',         color: 'bg-amber-100 text-amber-700' },
  revision_requested:     { label: 'Revision Requested',   color: 'bg-orange-100 text-orange-700' },
  coordinator_approved:   { label: 'Coord. Approved',      color: 'bg-teal-100 text-teal-700' },
  vp_approved:            { label: 'VP Approved',          color: 'bg-green-100 text-green-700' },
  rejected:               { label: 'Rejected',             color: 'bg-red-100 text-red-700' },
  completed:              { label: 'Completed',            color: 'bg-purple-100 text-purple-700' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-500' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${m.color}`}>{m.label}</span>;
}

// ─── KPI Card ─────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, color = 'blue', sub }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-green-50 text-green-600',
    amber:  'bg-amber-50 text-amber-600',
    red:    'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    teal:   'bg-teal-50 text-teal-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800">{value ?? '—'}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Create Plan Modal ────────────────────────────────────────
function CreatePlanModal({ onClose, onCreated, academicYearId }) {
  const [form, setForm] = useState({
    grade: '', class_id: '', section: '', subject_id: '', week_number: '',
    lesson_date: '', lesson_title: '', lesson_duration: 45, term_number: 1,
  });
  const [subjects, setSubjects]   = useState([]);
  const [classes,  setClasses]    = useState([]);
  const [saving,   setSaving]     = useState(false);

  const GRADES = ['Nursery','KG 1','KG 2','I','II','III','IV','V','VI','VII','VIII'];

  useEffect(() => {
    schoolConfigAPI.getSubjects().then(r => setSubjects(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.grade) return setClasses([]);
    schoolConfigAPI.getClasses()
      .then(r => setClasses((r.data || []).filter(c => c.grade === form.grade)))
      .catch(() => {});
  }, [form.grade]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.grade || !form.subject_id || !form.week_number || !form.lesson_date || !form.lesson_title) {
      return toast.error('Please fill all required fields');
    }
    setSaving(true);
    try {
      const res = await plannerAPI.createPlan({ ...form, academic_year_id: academicYearId || 3 });
      toast.success('Lesson plan created');
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-display font-bold text-slate-800 text-lg">New Lesson Plan</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Grade <span className="text-red-500">*</span></label>
              <select value={form.grade} onChange={e => set('grade', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">Select grade</option>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Class / Section</label>
              <select value={form.class_id} onChange={e => set('class_id', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">All sections</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}{c.section ? ` - ${c.section}` : ''}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Subject <span className="text-red-500">*</span></label>
              <select value={form.subject_id} onChange={e => set('subject_id', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">Select subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Term</label>
              <select value={form.term_number} onChange={e => set('term_number', parseInt(e.target.value))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                {[1,2,3,4].map(t => <option key={t} value={t}>Term {t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Week Number <span className="text-red-500">*</span></label>
              <input type="number" min="1" max="52" value={form.week_number}
                onChange={e => set('week_number', e.target.value)}
                placeholder="e.g. 24"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Lesson Date <span className="text-red-500">*</span></label>
              <input type="date" value={form.lesson_date}
                onChange={e => set('lesson_date', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Lesson Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.lesson_title}
              onChange={e => set('lesson_title', e.target.value)}
              placeholder="e.g. Introduction to Fractions"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Duration (minutes)</label>
            <input type="number" min="15" max="180" value={form.lesson_duration}
              onChange={e => set('lesson_duration', parseInt(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin"/> : <Save size={15}/>}
              {saving ? 'Creating…' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Review Modal (Coordinator) ───────────────────────────────
function ReviewModal({ plan, onClose, onDone }) {
  const [action, setAction]   = useState('approved');
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);

  const submit = async () => {
    if (!comment.trim()) return toast.error('Please add a review comment');
    setSaving(true);
    try {
      await plannerAPI.submitReview(plan.id, { action, review_comments: comment });
      toast.success(action === 'approved' ? 'Plan approved' : action === 'revision_requested' ? 'Revision requested' : 'Plan rejected');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-display font-bold text-slate-800">Review Plan</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-xs text-slate-500">Plan</p>
            <p className="font-semibold text-slate-800">{plan.lesson_title}</p>
            <p className="text-xs text-slate-500">{plan.teacher_name} · {plan.grade} · Week {plan.week_number}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Action</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'approved',           label: 'Approve',  color: 'green' },
                { value: 'revision_requested', label: 'Revise',   color: 'amber' },
                { value: 'rejected',           label: 'Reject',   color: 'red' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setAction(opt.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all
                    ${action === opt.value
                      ? opt.color === 'green' ? 'border-green-500 bg-green-50 text-green-700'
                        : opt.color === 'amber' ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Comment <span className="text-red-500">*</span></label>
            <textarea rows={4} value={comment} onChange={e => setComment(e.target.value)}
              placeholder="Provide specific feedback for the teacher…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={submit} disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin"/> : null}
              {saving ? 'Submitting…' : 'Submit Review'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Approval Modal (VP) ──────────────────────────────────────
function ApprovalModal({ plan, onClose, onDone }) {
  const [action, setAction]   = useState('approved');
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);

  const submit = async () => {
    if (!comment.trim()) return toast.error('Comment is required');
    setSaving(true);
    try {
      await plannerAPI.submitApproval(plan.id, { action, comments: comment });
      toast.success(action === 'approved' ? 'Plan approved by VP' : 'Plan returned');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-display font-bold text-slate-800">VP Approval</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="font-semibold text-slate-800">{plan.lesson_title}</p>
            <p className="text-xs text-slate-500">{plan.teacher_name} · {plan.grade} · {plan.coordinator_name && `Reviewed by ${plan.coordinator_name}`}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Decision</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'approved',            label: 'Approve', color: 'green' },
                { value: 'return_for_revision', label: 'Return',  color: 'amber' },
                { value: 'rejected',            label: 'Reject',  color: 'red' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setAction(opt.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all
                    ${action === opt.value
                      ? opt.color === 'green' ? 'border-green-500 bg-green-50 text-green-700'
                        : opt.color === 'amber' ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Comments <span className="text-red-500">*</span></label>
            <textarea rows={4} value={comment} onChange={e => setComment(e.target.value)}
              placeholder="VP comments…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin"/> : null}
              {saving ? 'Submitting…' : 'Submit Decision'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Plans Table ──────────────────────────────────────────────
function PlansTable({ plans, onSubmit, onView, loading }) {
  if (loading) return <div className="py-12 text-center text-slate-400 text-sm">Loading plans…</div>;
  if (!plans.length) return (
    <div className="py-16 text-center">
      <BookOpenCheck size={40} className="mx-auto text-slate-300 mb-3"/>
      <p className="text-slate-500 font-medium">No lesson plans found</p>
      <p className="text-slate-400 text-sm mt-1">Create your first plan using the button above</p>
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Title</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Grade / Subject</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Teacher</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Week</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Date</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Completion</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {plans.map(p => (
            <tr key={p.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 font-medium text-slate-800 max-w-[180px] truncate">{p.lesson_title}</td>
              <td className="px-4 py-3 text-slate-600">{p.grade} · <span className="text-slate-400">{p.subject_name}</span></td>
              <td className="px-4 py-3 text-slate-600">{p.teacher_name || '—'}</td>
              <td className="px-4 py-3 text-slate-600">W{p.week_number}</td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                {p.lesson_date ? new Date(p.lesson_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—'}
                {p.is_late_submission ? <span className="ml-1 text-red-400 text-xs">⚠ Late</span> : null}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full">
                    <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${p.completion_pct || 0}%` }}/>
                  </div>
                  <span className="text-xs text-slate-500">{p.completion_pct || 0}%</span>
                </div>
              </td>
              <td className="px-4 py-3"><StatusBadge status={p.status}/></td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => onView(p)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50" title="View">
                    <Eye size={15}/>
                  </button>
                  {(p.status === 'draft' || p.status === 'revision_requested') && (
                    <button onClick={() => onSubmit(p)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50" title="Submit for review">
                      <Send size={15}/>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Review Queue (Coordinator) ───────────────────────────────
function ReviewQueue({ onDone }) {
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getPendingReviews()
      .then(r => setQueue(r.data || []))
      .catch(() => toast.error('Failed to load review queue'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-12 text-center text-slate-400">Loading review queue…</div>;
  if (!queue.length) return (
    <div className="py-16 text-center">
      <CheckCircle size={40} className="mx-auto text-green-300 mb-3"/>
      <p className="text-slate-500 font-medium">No plans pending review</p>
      <p className="text-slate-400 text-sm mt-1">All submitted plans have been reviewed</p>
    </div>
  );

  return (
    <>
      {selected && (
        <ReviewModal plan={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); onDone(); }}/>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Plan</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Teacher</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Grade</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Week</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Waiting</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {queue.map(p => (
              <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${p.hours_waiting >= 24 ? 'bg-red-50/50' : ''}`}>
                <td className="px-4 py-3 font-medium text-slate-800 max-w-[200px] truncate">{p.lesson_title}</td>
                <td className="px-4 py-3 text-slate-600">{p.teacher_name}</td>
                <td className="px-4 py-3 text-slate-600">{p.grade}</td>
                <td className="px-4 py-3 text-slate-600">W{p.week_number}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${p.hours_waiting >= 24 ? 'text-red-600' : p.hours_waiting >= 12 ? 'text-amber-600' : 'text-slate-500'}`}>
                    {p.hours_waiting >= 24
                      ? `⚠ ${Math.round(p.hours_waiting)}h (SLA breach)`
                      : `${Math.round(p.hours_waiting)}h`
                    }
                  </span>
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status}/></td>
                <td className="px-4 py-3">
                  <button onClick={() => setSelected(p)}
                    className="px-3 py-1.5 bg-primary-600 text-white text-xs font-semibold rounded-lg hover:bg-primary-700">
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Approval Queue (VP) ──────────────────────────────────────
function ApprovalQueue({ onDone }) {
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getPendingApprovals()
      .then(r => setQueue(r.data || []))
      .catch(() => toast.error('Failed to load approval queue'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-12 text-center text-slate-400">Loading approval queue…</div>;
  if (!queue.length) return (
    <div className="py-16 text-center">
      <CheckSquare size={40} className="mx-auto text-green-300 mb-3"/>
      <p className="text-slate-500 font-medium">No plans awaiting approval</p>
    </div>
  );

  return (
    <>
      {selected && (
        <ApprovalModal plan={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); onDone(); }}/>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Plan</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Teacher</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Grade</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Coordinator</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Coord. Comments</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Waiting</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {queue.map(p => (
              <tr key={p.id} className={`hover:bg-slate-50 ${p.hours_since_review >= 48 ? 'bg-red-50/50' : ''}`}>
                <td className="px-4 py-3 font-medium text-slate-800 max-w-[180px] truncate">{p.lesson_title}</td>
                <td className="px-4 py-3 text-slate-600">{p.teacher_name}</td>
                <td className="px-4 py-3 text-slate-600">{p.grade}</td>
                <td className="px-4 py-3 text-slate-500">{p.coordinator_name || '—'}</td>
                <td className="px-4 py-3 text-slate-500 max-w-[160px] truncate text-xs">{p.coordinator_comments || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${p.hours_since_review >= 48 ? 'text-red-600' : 'text-slate-500'}`}>
                    {p.hours_since_review >= 48 ? `⚠ ${Math.round(p.hours_since_review)}h` : `${Math.round(p.hours_since_review)}h`}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => setSelected(p)}
                    className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700">
                    Decide
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Plan Detail Drawer ───────────────────────────────────────
function PlanDetailDrawer({ planId, onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    plannerAPI.getPlan(planId)
      .then(r => setPlan(r.data))
      .catch(() => toast.error('Failed to load plan'))
      .finally(() => setLoading(false));
  }, [planId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}/>
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto h-full">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-slate-800">Plan Detail</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="animate-spin text-primary-500" size={32}/>
          </div>
        ) : plan ? (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-xl font-bold text-slate-800">{plan.lesson_title}</h4>
                <StatusBadge status={plan.status}/>
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
                <span>{plan.teacher_name}</span>
                <span>·</span>
                <span>{plan.grade} — {plan.subject_name}</span>
                <span>·</span>
                <span>Week {plan.week_number}</span>
                <span>·</span>
                <span>{plan.lesson_duration} min</span>
              </div>
            </div>

            {/* Completion bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Plan Completion</span>
                <span className="font-semibold text-slate-700">{plan.completion_pct || 0}%</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full">
                <div className="h-2 rounded-full bg-primary-500 transition-all" style={{ width: `${plan.completion_pct || 0}%` }}/>
              </div>
            </div>

            {/* 5 Stages */}
            {plan.stages?.length > 0 && (
              <div>
                <h5 className="text-sm font-bold text-slate-700 mb-3">Lesson Stages</h5>
                <div className="space-y-3">
                  {plan.stages.map(s => (
                    <div key={s.id} className={`rounded-xl p-4 border ${s.is_complete ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${s.is_complete ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>{s.stage_number}</span>
                        <span className="font-semibold text-slate-700 text-sm">{s.stage_name}</span>
                        {s.estimated_duration && <span className="ml-auto text-xs text-slate-400">{s.estimated_duration} min</span>}
                      </div>
                      {s.objective && <p className="text-xs text-slate-600 mt-1"><span className="font-medium">Objective:</span> {s.objective}</p>}
                      {s.activity_description && <p className="text-xs text-slate-600 mt-1"><span className="font-medium">Activity:</span> {s.activity_description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status History */}
            {plan.status_history?.length > 0 && (
              <div>
                <h5 className="text-sm font-bold text-slate-700 mb-3">Status History</h5>
                <div className="space-y-2">
                  {plan.status_history.map(h => (
                    <div key={h.id} className="flex items-start gap-3 text-xs">
                      <span className="text-slate-400 whitespace-nowrap pt-0.5">
                        {new Date(h.changed_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                      </span>
                      <div>
                        <span className="font-medium text-slate-700">{h.changed_by_name || 'System'}</span>
                        <span className="text-slate-400"> changed status to </span>
                        <StatusBadge status={h.to_status}/>
                        {h.reason && <p className="text-slate-500 mt-0.5 italic">"{h.reason}"</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-20 text-center text-slate-400">Plan not found</div>
        )}
      </div>
    </div>
  );
}

// ─── Observation Criteria ─────────────────────────────────────
const OBS_CRITERIA = [
  { key: 'score_planning_quality',       label: 'Planning Quality' },
  { key: 'score_content_knowledge',      label: 'Content Knowledge' },
  { key: 'score_classroom_management',   label: 'Classroom Management' },
  { key: 'score_student_engagement',     label: 'Student Engagement' },
  { key: 'score_differentiation',        label: 'Differentiation' },
  { key: 'score_assessment_practices',   label: 'Assessment Practices' },
  { key: 'score_questioning_techniques', label: 'Questioning Techniques' },
  { key: 'score_use_of_resources',       label: 'Use of Resources' },
  { key: 'score_reflection_quality',     label: 'Reflection Quality' },
];

function ScoreSelector({ value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`w-9 h-9 rounded-lg text-sm font-bold border-2 transition-all
            ${value === n
              ? n <= 1 ? 'border-red-400 bg-red-50 text-red-700'
                : n === 2 ? 'border-amber-400 bg-amber-50 text-amber-700'
                : n === 3 ? 'border-blue-400 bg-blue-50 text-blue-700'
                : 'border-green-400 bg-green-50 text-green-700'
              : 'border-slate-200 text-slate-400 hover:border-slate-300'
            }`}>
          {n}
        </button>
      ))}
    </div>
  );
}

// ─── Create Observation Modal ─────────────────────────────────
function CreateObservationModal({ onClose, onCreated, academicYearId }) {
  const [form, setForm] = useState({
    teacher_id: '', observation_date: '', grade: '',
    overall_comment: '', strength_areas: '', improvement_areas: '',
    observation_start_time: '', observation_end_time: '',
    ...Object.fromEntries(OBS_CRITERIA.map(c => [c.key, null])),
  });
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    import('../services/api').then(({ default: api }) =>
      api.get('/employees?role=user').then(r => setTeachers(r.data || [])).catch(() => {})
    );
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalFilled = OBS_CRITERIA.filter(c => form[c.key] !== null).length;
  const totalScore  = OBS_CRITERIA.reduce((sum, c) => sum + (form[c.key] || 0), 0);
  const scorePct    = totalFilled > 0 ? Math.round((totalScore / (totalFilled * 4)) * 100) : 0;

  const submit = async () => {
    if (!form.teacher_id || !form.observation_date || !form.overall_comment.trim()) {
      return toast.error('Teacher, date, and overall comment are required');
    }
    setSaving(true);
    try {
      const r = await plannerAPI.createObservation({ ...form, academic_year_id: academicYearId || 3 });
      toast.success('Observation submitted');
      onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit observation');
    } finally {
      setSaving(false);
    }
  };

  const GRADES = ['Nursery','KG 1','KG 2','I','II','III','IV','V','VI','VII','VIII'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-slate-800 text-lg flex items-center gap-2">
            <ScanEye size={18} className="text-primary-600"/> New Classroom Observation
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Teacher <span className="text-red-500">*</span></label>
              <select value={form.teacher_id} onChange={e => set('teacher_id', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">Select teacher</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Date <span className="text-red-500">*</span></label>
              <input type="date" value={form.observation_date} onChange={e => set('observation_date', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Grade</label>
              <select value={form.grade} onChange={e => set('grade', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">Select grade</option>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Start Time</label>
              <input type="time" value={form.observation_start_time} onChange={e => set('observation_start_time', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">End Time</label>
              <input type="time" value={form.observation_end_time} onChange={e => set('observation_end_time', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
          </div>

          {/* 9-criteria scoring */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-700">Scoring Rubric <span className="font-normal text-slate-400">(1=Needs Improvement · 2=Developing · 3=Proficient · 4=Outstanding)</span></p>
              {totalFilled > 0 && (
                <span className={`text-xs font-bold px-2 py-1 rounded-full
                  ${scorePct >= 75 ? 'bg-green-100 text-green-700' : scorePct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {scorePct}% ({totalScore}/{totalFilled * 4})
                </span>
              )}
            </div>
            <div className="space-y-3">
              {OBS_CRITERIA.map(c => (
                <div key={c.key} className="flex items-center justify-between gap-4 py-2 border-b border-slate-50">
                  <span className="text-sm text-slate-700 flex-1">{c.label}</span>
                  <ScoreSelector value={form[c.key]} onChange={v => set(c.key, v)}/>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Strengths Observed</label>
            <textarea rows={2} value={form.strength_areas} onChange={e => set('strength_areas', e.target.value)}
              placeholder="Key strengths demonstrated…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Areas for Improvement</label>
            <textarea rows={2} value={form.improvement_areas} onChange={e => set('improvement_areas', e.target.value)}
              placeholder="Specific areas to work on…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Overall Comment <span className="text-red-500">*</span></label>
            <textarea rows={3} value={form.overall_comment} onChange={e => set('overall_comment', e.target.value)}
              placeholder="Overall assessment of the lesson…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          </div>

          <div className="flex gap-3 pt-2 border-t border-slate-100">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={submit} disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <ScanEye size={14}/>}
              {saving ? 'Submitting…' : 'Submit Observation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Observation Detail Drawer ────────────────────────────────
function ObservationDetailDrawer({ obs, onClose, onAcknowledge, isTeacher }) {
  const [acking, setAcking] = useState(false);

  const radarData = OBS_CRITERIA.map(c => ({
    subject: c.label.replace(' ', '\n'),
    score: obs[c.key] || 0,
    fullMark: 4,
  }));

  const acknowledge = async () => {
    setAcking(true);
    try {
      await plannerAPI.acknowledgeObservation(obs.id);
      toast.success('Observation acknowledged');
      onAcknowledge();
    } catch {
      toast.error('Failed to acknowledge');
    } finally {
      setAcking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}/>
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto h-full">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-slate-800 flex items-center gap-2">
            <ScanEye size={16} className="text-primary-600"/> Classroom Observation
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-bold text-slate-800 text-lg">{obs.teacher_name}</p>
              <p className="text-sm text-slate-500">
                Observed by {obs.observer_name} · {obs.grade || 'N/A'} ·{' '}
                {new Date(obs.observation_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0
              ${obs.status === 'acknowledged' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {obs.status === 'acknowledged' ? 'Acknowledged' : 'Pending Acknowledgement'}
            </span>
          </div>

          {/* Score summary */}
          {obs.observation_score_pct !== null && (
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-600">{obs.observation_score_pct}%</p>
                <p className="text-xs text-slate-400">Overall Score</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-700">{obs.total_score || 0}</p>
                <p className="text-xs text-slate-400">/ {OBS_CRITERIA.filter(c => obs[c.key]).length * 4} pts</p>
              </div>
              <div className="flex-1">
                <div className="w-full h-3 bg-slate-200 rounded-full">
                  <div className={`h-3 rounded-full transition-all
                    ${obs.observation_score_pct >= 75 ? 'bg-green-500' : obs.observation_score_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${obs.observation_score_pct}%` }}/>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {obs.observation_score_pct >= 75 ? 'Outstanding / Proficient'
                    : obs.observation_score_pct >= 50 ? 'Developing' : 'Needs Improvement'}
                </p>
              </div>
            </div>
          )}

          {/* Radar chart */}
          {OBS_CRITERIA.some(c => obs[c.key]) && (
            <div>
              <p className="text-xs font-bold text-slate-600 mb-2">Criteria Breakdown</p>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e2e8f0"/>
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#64748b' }}/>
                  <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fontSize: 9 }}/>
                  <Radar name="Score" dataKey="score" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.2}/>
                  <Tooltip formatter={v => [`${v}/4`, 'Score']}/>
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Criteria scores table */}
          <div>
            <p className="text-xs font-bold text-slate-600 mb-2">Detailed Scores</p>
            <div className="space-y-2">
              {OBS_CRITERIA.map(c => {
                const score = obs[c.key];
                if (!score) return null;
                const pct = (score / 4) * 100;
                return (
                  <div key={c.key} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-40 flex-shrink-0">{c.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full">
                      <div className={`h-2 rounded-full ${score >= 3 ? 'bg-green-500' : score === 2 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${pct}%` }}/>
                    </div>
                    <span className={`text-xs font-bold w-6 text-right
                      ${score >= 3 ? 'text-green-600' : score === 2 ? 'text-amber-600' : 'text-red-600'}`}>
                      {score}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comments */}
          {obs.strength_areas && (
            <div className="p-4 bg-green-50 rounded-xl border border-green-100">
              <p className="text-xs font-bold text-green-700 mb-1">Strengths</p>
              <p className="text-sm text-slate-700">{obs.strength_areas}</p>
            </div>
          )}
          {obs.improvement_areas && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <p className="text-xs font-bold text-amber-700 mb-1">Areas for Improvement</p>
              <p className="text-sm text-slate-700">{obs.improvement_areas}</p>
            </div>
          )}
          {obs.overall_comment && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs font-bold text-slate-600 mb-1">Overall Comment</p>
              <p className="text-sm text-slate-700">{obs.overall_comment}</p>
            </div>
          )}

          {/* Acknowledge button for teacher */}
          {isTeacher && obs.status !== 'acknowledged' && (
            <button onClick={acknowledge} disabled={acking}
              className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-60">
              {acking ? <Loader2 size={15} className="animate-spin"/> : <ThumbsUp size={15}/>}
              {acking ? 'Acknowledging…' : 'Acknowledge Observation'}
            </button>
          )}
          {obs.teacher_acknowledged_at && (
            <p className="text-center text-xs text-green-600 font-medium">
              ✓ Acknowledged on {new Date(obs.teacher_acknowledged_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Observations Tab ─────────────────────────────────────────
function ObservationsTab({ role, yearId, onDone }) {
  const [obs,         setObs]         = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [selected,    setSelected]    = useState(null);

  const isCoordOrAbove = ['management','coordinator','admin','super_admin'].includes(role);
  const isTeacher      = role === 'user' || role === 'teacher';

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getObservations({ academic_year_id: yearId || 3 })
      .then(r => setObs(r.data || []))
      .catch(() => toast.error('Failed to load observations'))
      .finally(() => setLoading(false));
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  const pending    = obs.filter(o => o.status !== 'acknowledged').length;
  const avgScore   = obs.filter(o => o.observation_score_pct).length
    ? Math.round(obs.filter(o => o.observation_score_pct).reduce((s, o) => s + o.observation_score_pct, 0) / obs.filter(o => o.observation_score_pct).length)
    : null;

  if (loading) return <div className="py-12 text-center text-slate-400 text-sm">Loading observations…</div>;

  return (
    <>
      {showCreate && (
        <CreateObservationModal
          academicYearId={yearId || 3}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {selected && (
        <ObservationDetailDrawer
          obs={selected}
          isTeacher={isTeacher}
          onClose={() => setSelected(null)}
          onAcknowledge={() => { setSelected(null); load(); }}
        />
      )}

      {/* Summary row */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
        <div className="text-center px-4">
          <p className="text-2xl font-bold text-slate-800">{obs.length}</p>
          <p className="text-xs text-slate-400">Total</p>
        </div>
        <div className="h-8 w-px bg-slate-200"/>
        <div className="text-center px-4">
          <p className="text-2xl font-bold text-amber-600">{pending}</p>
          <p className="text-xs text-slate-400">Pending Ack.</p>
        </div>
        <div className="h-8 w-px bg-slate-200"/>
        <div className="text-center px-4">
          <p className="text-2xl font-bold text-primary-600">{avgScore ?? '—'}{avgScore ? '%' : ''}</p>
          <p className="text-xs text-slate-400">Avg Score</p>
        </div>
        {isCoordOrAbove && (
          <button onClick={() => setShowCreate(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700">
            <Plus size={15}/> New Observation
          </button>
        )}
      </div>

      {obs.length === 0 ? (
        <div className="py-16 text-center">
          <ScanEye size={40} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-slate-500 font-medium">No observations yet</p>
          {isCoordOrAbove && <p className="text-slate-400 text-sm mt-1">Record a classroom observation using the button above</p>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Teacher</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Observer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Grade</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {obs.map(o => (
                <tr key={o.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800">{o.teacher_name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{o.observer_name}</td>
                  <td className="px-4 py-3 text-slate-600">{o.grade || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {new Date(o.observation_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}
                  </td>
                  <td className="px-4 py-3">
                    {o.observation_score_pct !== null ? (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                        ${o.observation_score_pct >= 75 ? 'bg-green-100 text-green-700'
                          : o.observation_score_pct >= 50 ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'}`}>
                        {o.observation_score_pct}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                      ${o.status === 'acknowledged' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {o.status === 'acknowledged' ? 'Acknowledged' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelected(o)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50">
                      <Eye size={15}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────
const TEACHER_ROLE_ALIASES = ['user', 'teacher'];
const COORD_ROLE_ALIASES   = ['management', 'coordinator'];
const ADMIN_ROLE_ALIASES   = ['admin', 'super_admin'];

const TABS = [
  { key: 'dashboard',    label: 'Dashboard',      icon: BarChart3,     roles: [...TEACHER_ROLE_ALIASES, ...COORD_ROLE_ALIASES, ...ADMIN_ROLE_ALIASES] },
  { key: 'plans',        label: 'My Plans',       icon: FileText,      roles: [...TEACHER_ROLE_ALIASES, ...COORD_ROLE_ALIASES, ...ADMIN_ROLE_ALIASES] },
  { key: 'review',       label: 'Review Queue',   icon: CheckCircle,   roles: [...COORD_ROLE_ALIASES, ...ADMIN_ROLE_ALIASES] },
  { key: 'approval',     label: 'Approval Queue', icon: CheckSquare,   roles: [...COORD_ROLE_ALIASES, ...ADMIN_ROLE_ALIASES] },
  { key: 'observations', label: 'Observations',   icon: ScanEye,       roles: [...TEACHER_ROLE_ALIASES, ...COORD_ROLE_ALIASES, ...ADMIN_ROLE_ALIASES] },
  { key: 'settings',     label: 'Settings',       icon: Settings,      roles: [...ADMIN_ROLE_ALIASES] },
];

export default function LessonPlannerWF() {
  const { user } = useAuth();
  const { yearId } = useContext(YearContext);
  const navigate = useNavigate();
  const role = user?.role || 'user';

  const [tab,         setTab]         = useState('dashboard');
  const [plans,       setPlans]       = useState([]);
  const [stats,       setStats]       = useState(null);
  const [compliance,  setCompliance]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [submitTarget,setSubmitTarget]= useState(null);
  const [statusFilter,setStatusFilter]= useState('');

  const isAdmin = ['admin','super_admin'].includes(role);
  const isCoord = role === 'management' || role === 'coordinator';

  const loadPlans = useCallback(() => {
    setLoading(true);
    const params = { academic_year_id: yearId || 3 };
    if (statusFilter) params.status = statusFilter;
    const controller = new AbortController();
    Promise.all([
      plannerAPI.getPlans(params),
      plannerAPI.getStats({ academic_year_id: yearId || 3 }),
      plannerAPI.getCompliance({ academic_year_id: yearId || 3 }),
    ]).then(([p, s, c]) => {
      if (controller.signal.aborted) return;
      setPlans(p.data.data || []);
      setStats(s.data);
      setCompliance(c.data);
    }).catch(err => {
      if (controller.signal.aborted) return;
      const status = err?.response?.status;
      if (status !== 404) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load plan data';
        toast.error(msg);
        console.error('loadPlans failed:', status, err?.response?.data || err?.message);
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [yearId, statusFilter]);

  useEffect(() => loadPlans(), [loadPlans]);

  const handleSubmitPlan = async (plan) => {
    if (!window.confirm(`Submit "${plan.lesson_title}" for review?`)) return;
    try {
      await plannerAPI.submitPlan(plan.id);
      toast.success('Plan submitted for review');
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit plan');
    }
  };

  const visibleTabs = TABS.filter(t => t.roles.includes(role));
  const bs = stats?.by_status || {};
  const wk = stats?.this_week || {};

  // Default to 'review' tab for coordinators
  useEffect(() => {
    if ((isCoord || isAdmin) && tab === 'plans') { /* keep plans as default */ }
  }, [isCoord, isAdmin, tab]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold text-slate-800 flex items-center gap-2">
            <BookOpenCheck size={24} className="text-primary-600"/>
            Toddle Lesson Planner
          </h2>
          <p className="text-slate-500 text-sm mt-1">Plan · Submit · Review · Approve · Execute · Reflect</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-sm">
          <Plus size={16}/> New Lesson Plan
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total Plans" value={Object.values(bs).reduce((a,b) => a+b, 0)} icon={FileText} color="blue"/>
        <KpiCard label="Draft" value={bs.draft || 0} icon={Clock} color="amber"/>
        <KpiCard label="Pending Review" value={stats?.pending_review || 0} icon={AlertTriangle} color="amber"
          sub={stats?.pending_review > 0 ? 'Awaiting coordinator' : undefined}/>
        <KpiCard label="Pending VP" value={stats?.pending_approval || 0} icon={ChevronRight} color="teal"/>
        <KpiCard label="Approved" value={(bs.vp_approved || 0) + (bs.completed || 0)} icon={CheckCircle} color="green"/>
        <KpiCard label="This Week" value={`${wk.submitted || 0}/${wk.total || 0}`} icon={TrendingUp} color="purple"
          sub={compliance ? `${compliance.compliance_pct}% compliance` : undefined}/>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100 px-4 pt-4 gap-1">
          {visibleTabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-all
                ${tab === t.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}>
              {t.icon && <t.icon size={13} className="flex-shrink-0"/>}
              {t.label}
              {t.key === 'review' && stats?.pending_review > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{stats.pending_review}</span>
              )}
              {t.key === 'approval' && stats?.pending_approval > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5">{stats.pending_approval}</span>
              )}
              {t.key === 'observations' && (role === 'user' || role === 'teacher') && (
                <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">!</span>
              )}
            </button>
          ))}

          {/* Status filter on plans tab */}
          {tab === 'plans' && (
            <div className="ml-auto flex items-center gap-2 pb-2">
              <Filter size={13} className="text-slate-400"/>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-300">
                <option value="">All statuses</option>
                {Object.keys(STATUS_META).map(s => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={['dashboard','settings'].includes(tab) ? 'p-5' : 'p-0'}>
          {tab === 'dashboard' && (
            <LessonPlannerDashboard role={role} yearId={yearId}/>
          )}
          {tab === 'plans' && (
            <PlansTable
              plans={plans}
              loading={loading}
              onSubmit={handleSubmitPlan}
              onView={p => navigate(`/lesson-planner/${p.id}`)}
            />
          )}
          {tab === 'review' && (
            <ReviewQueue onDone={loadPlans}/>
          )}
          {tab === 'approval' && (
            <ApprovalQueue onDone={loadPlans}/>
          )}
          {tab === 'observations' && (
            <ObservationsTab role={role} yearId={yearId} onDone={loadPlans}/>
          )}
          {tab === 'settings' && (
            <LessonPlannerSettings yearId={yearId}/>
          )}
        </div>
      </div>


      {/* Modals */}
      {showCreate && (
        <CreatePlanModal
          academicYearId={yearId || 3}
          onClose={() => setShowCreate(false)}
          onCreated={(plan) => { setShowCreate(false); navigate(`/lesson-planner/${plan.id}`); }}
        />
      )}
    </div>
  );
}
